import React, { useEffect, useRef } from 'react';
import { Table, Tooltip, Input, Button } from 'antd';
import { FilterFilled, InfoCircleOutlined } from '@ant-design/icons';
import AnimatedProgressBar from '../../UI/animatedProgressBar';


const AnalyticsTable = ({ mergedData, loading, showUniqueData = true }) => {
  const getNumberFilterProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Min"
          value={selectedKeys[0]?.min || ''}
          onChange={e => setSelectedKeys([{ min: e.target.value, max: selectedKeys[0]?.max || '' }])}
          style={{ marginBottom: 8, display: 'block', width: 120 }}
          type="number"
        />
        <Input
          placeholder="Max"
          value={selectedKeys[0]?.max || ''}
          onChange={e => setSelectedKeys([{ min: selectedKeys[0]?.min || '', max: e.target.value }])}
          style={{ marginBottom: 8, display: 'block', width: 120 }}
          type="number"
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Filter
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            confirm();
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const { min, max } = value;
      const recordValue = record[dataIndex];
      
      if (min && max) {
        return recordValue >= Number(min) && recordValue <= Number(max);
      }
      if (min) {
        return recordValue >= Number(min);
      }
      if (max) {
        return recordValue <= Number(max);
      }
      return true;
    }
  });

  const getColorByRate = (rate) => {
    if (rate >= 70) return '#52c41a';
    if (rate >= 40) return '#faad14';
    return '#ff4d4f';
  };

  const columns = [
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>Organization</span>
        </div>
      ),
      dataIndex: 'organization_name',
      key: 'organization_name',
      filterSearch: false,
      sorter: (a, b) => a.organization_name.localeCompare(b.organization_name),
      render: (text) => <span style={{ color: '#334155', fontWeight: '500' }}>{text}</span>
    },
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>{showUniqueData ? 'Unique' : 'Total'} Leads</span>
        </div>
      ),
      dataIndex: 'total_unique_leads',
      key: 'total_unique_leads',
      sorter: (a, b) => a.total_unique_leads - b.total_unique_leads,
      ...getNumberFilterProps('total_unique_leads'),
      render: (value) => <span style={{ color: '#334155' }}>{value}</span>
    },
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>{showUniqueData ? 'Unique' : 'Total'} Bookings</span>
        </div>
      ),
      dataIndex: 'total_bookings',
      key: 'total_bookings',
      sorter: (a, b) => a.total_bookings - b.total_bookings,
      ...getNumberFilterProps('total_bookings'),
      render: (value) => <span style={{ color: '#334155' }}>{value}</span>
    },
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>{showUniqueData ? 'Unique' : 'Total'} Abandonments</span>
        </div>
      ),
      dataIndex: 'total_abandonments',
      key: 'total_abandonments',
      sorter: (a, b) => a.total_abandonments - b.total_abandonments,
      ...getNumberFilterProps('total_abandonments'),
      render: (value) => <span style={{ color: '#334155' }}>{value}</span>
    },
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>{showUniqueData ? 'Unique' : 'Total'} Booking Rate</span>
        </div>
      ),
      dataIndex: 'booking_rate_percentage',
      key: 'booking_rate_percentage',
      sorter: (a, b) => a.booking_rate_percentage - b.booking_rate_percentage,
      ...getNumberFilterProps('booking_rate_percentage'),
      render: (value) => (
        <AnimatedProgressBar
          percentage={value}
          height="20px"
          showText={true}
          textColor="auto"
          width="100%"
          colorScheme="booking"
        />
      ),
    },
    {
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#1e293b', fontWeight: '500' }}>{showUniqueData ? 'Unique' : 'Total'} Abandonment Rate</span>
        </div>
      ),
      dataIndex: 'abandonment_rate_percentage',
      key: 'abandonment_rate_percentage',
      sorter: (a, b) => a.abandonment_rate_percentage - b.abandonment_rate_percentage,
      ...getNumberFilterProps('abandonment_rate_percentage'),
      render: (value) => (
        <AnimatedProgressBar
          percentage={value}
          height="20px"
          showText={true}
          textColor="auto"
          colorScheme="abandonment"
          width="100%"
        />
      ),
    },
  ];

  const tableStyle = {
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <Table
        dataSource={mergedData}
        columns={columns}
        rowKey="organization_name"
        pagination={false}
        bordered
        loading={loading}
        style={tableStyle}
        rowClassName={() => 'analytics-table-row'}
        scroll={{ x: true }}
        locale={{ 
          emptyText: (
            <div style={{ 
              padding: '24px',
              color: '#64748b',
              fontSize: '14px',
              textAlign: 'center' 
            }}>
              No data available
            </div>
          ) 
        }}
        summary={(pageData) => {
          if (pageData.length === 0) return null;
          
          const sums = pageData.reduce((acc, curr) => ({
            total_unique_leads: acc.total_unique_leads + curr.total_unique_leads,
            total_bookings: acc.total_bookings + curr.total_bookings,
            total_abandonments: acc.total_abandonments + curr.total_abandonments,
          }), {
            total_unique_leads: 0,
            total_bookings: 0,
            total_abandonments: 0,
          });

          const count = pageData.length;
          const totalCompletionRate = (sums.total_bookings / sums.total_unique_leads) * 100;
          const totalAbandonmentRate = (sums.total_abandonments / sums.total_unique_leads) * 100;
          
          return (
            <Table.Summary fixed>
              <Table.Summary.Row className="total-row">
                <Table.Summary.Cell index={0}>
                  Total ({count} rows)
                </Table.Summary.Cell>
                <Table.Summary.Cell style={{cursor:"pointer"}} index={1}>
                  <Tooltip title={`Sum of ${showUniqueData ? 'Unique' : 'Total'} Leads across all organizations in the selected date range`}>
                    {sums.total_unique_leads}
                    <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                  </Tooltip>
                </Table.Summary.Cell>
                <Table.Summary.Cell style={{cursor:"pointer"}} index={2}>
                  <Tooltip title={`Sum of ${showUniqueData ? 'Unique' : 'Total'} Bookings across all organizations in the selected date range`}>
                    {sums.total_bookings}
                    <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                  </Tooltip>
                </Table.Summary.Cell>
                <Table.Summary.Cell style={{cursor:"pointer"}} index={3}>
                  <Tooltip title={`Sum of ${showUniqueData ? 'Unique' : 'Total'} Abandonments across all organizations in the selected date range`}>
                    {sums.total_abandonments}
                    <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                  </Tooltip>
                </Table.Summary.Cell>
                <Table.Summary.Cell style={{cursor:"pointer"}} index={4}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ width: '70%' }}>
                      <AnimatedProgressBar
                        percentage={totalCompletionRate}
                        height="20px"
                        showText={false}
                        textColor="auto"
                        width="100%"
                        colorScheme="booking"
                      />
                    </div>
                    <Tooltip title={`Overall ${showUniqueData ? 'Unique' : 'Total'} Booking Rate calculated as: (${showUniqueData ? 'Unique' : 'Total'} Bookings ÷ ${showUniqueData ? 'Unique' : 'Total'} Leads) × 100%`}>
                      <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                        {totalCompletionRate.toFixed(2)}%
                        <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                      </span>
                    </Tooltip>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell style={{cursor:"pointer"}} index={5}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ width: '70%' }}>
                      <AnimatedProgressBar
                        percentage={totalAbandonmentRate}
                        height="20px"
                        showText={false}
                        textColor="auto"
                        width="100%"
                        colorScheme="abandonment"
                      />
                    </div>
                    <Tooltip title={`Overall ${showUniqueData ? 'Unique' : 'Total'} Abandonment Rate calculated as: (${showUniqueData ? 'Unique' : 'Total'} Abandonments ÷ ${showUniqueData ? 'Unique' : 'Total'} Leads) × 100%`}>
                      <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                        {totalAbandonmentRate.toFixed(2)}%
                        <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                      </span>
                    </Tooltip>
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
};

export default AnalyticsTable; 